/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  styled,
  Avatar,
  IconButton,
  Badge,
  BadgeProps,
  Typography,
  Stack
} from "@mui/material";

interface DropzoneInterface {
  handleDrop: (file: any) => void;
  image: string;
  isDark?: boolean;
  isFull?: boolean;
  rounded?: boolean;
}

interface BadgeInterface extends BadgeProps {
  isFull?: boolean;
}

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  border: `2px solid ${theme.palette.grey[300]}`,
  "& .MuiAvatar-img": {
    objectFit: "contain"
  }
}));

const StyledIconButton = styled(IconButton)(({ theme }: any) => ({
  borderRadius: "50px",
  border: `3px solid ${theme.palette.grey[300]}`,
  backgroundColor: theme.palette.white
}));

const CustomBadge = styled(Badge, {
  shouldForwardProp: prop => prop !== "isFull",
  name: "Badge"
})<BadgeInterface>(({ isFull }) => ({
  width: isFull ? "100%" : 120,
  "& .MuiBadge-badge": {
    bottom: isFull ? 0 : "14%",
    right: isFull ? 0 : "14%"
  }
}));

const ImageBox = styled(Box)(({ theme }: any) => ({
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  height: 170,
  width: "100%",
  border: `2px solid ${theme.palette.grey[300]}`
}));

const Dropzone = ({
  handleDrop,
  image,
  isDark,
  isFull = false,
  rounded = false
}: DropzoneInterface) => {
  const onDrop = useCallback((acceptedFiles: any) => {
    handleDrop(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop
  });

  const avatar = (
    <StyledAvatar
      src={image}
      sx={{
        width: 120,
        height: 120,
        backgroundColor: isDark ? "#000" : "#fff"
      }}
    />
  );

  const fullWidthBox = (
    <ImageBox style={{ backgroundImage: `url(${image})` }} />
  );

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {image
        ? <CustomBadge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <StyledIconButton>
                <i className="far fa-pen icon-md" />
              </StyledIconButton>
            }
            isFull={isFull}
          >
            {isFull ? fullWidthBox : avatar}
          </CustomBadge>
        : <Box
            height={rounded ? "78px" : "163px"}
            width={rounded ? "78px" : "196px"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            className={`border-1-${rounded
              ? "primary"
              : "borderGrey"} background-${rounded
              ? "white"
              : "backgroundGreyLight"}`}
            borderRadius={rounded ? 10 : 3}
          >
            {rounded
              ? <React.Fragment>
                  <i className="fad fa-camera font-green fa-lg " />
                  <Typography>Upload</Typography>
                </React.Fragment>
              : <Stack spacing={1}>
                  <Box
                    px={1.2}
                    py={1.5}
                    textAlign="center"
                    borderRadius={1.2}
                    className="background-grey"
                  >
                    <i className="fad fa-camera font-white fa-lg" />
                  </Box>
                  <Typography>150x150</Typography>
                </Stack>}
          </Box>}
    </div>
  );
};

export default Dropzone;
